import React, { forwardRef, useEffect, useRef, useState } from "react";
import axios from "axios";
import { global, globalMenu } from "@shared/libs/Global";
import { fnGetDataSet } from "@shared/api/restApi";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import SnButton from "@shared/components/SnButton";

interface Props {
  // 저장된 행: 경비번호+항번으로 바로 올리고(sys_attachfile) 지운다.
  no_expense?: string;
  no_line?: number;
  onCountChanged?: (count: number) => void;
  // 저장 전 행: 고른 파일을 화면이 들고 있다가 저장 후 올린다.
  pendingFiles?: File[];
  onPendingChanged?: (files: File[]) => void;
}

interface Preview {
  key: string;
  nm_file: string;
  url: string;
}

const IMAGE_EXT = /\.(jpe?g|png|gif|bmp|webp)$/i;

// 다운로드 API 응답에는 Content-Type 이 없어, 새 탭으로 열면 이미지가 아닌 바이트 글자로 보인다.
// 확장자로 이미지 형식을 붙여 Blob 을 만든다.
const imageMime = (fileName: string) => {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  return `image/${ext === "jpg" ? "jpeg" : ext}`;
};

// 썸네일 클릭 = 화면 위에 크게 보기(새 탭 대신). 아무 곳이나 클릭하거나 ESC 로 닫는다.
const PreviewList = ({ previews }: { previews: Preview[] }) => {
  const [enlarged, setEnlarged] = useState<Preview | null>(null);

  useEffect(() => {
    if (!enlarged) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setEnlarged(null);
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [enlarged]);

  return (
    <div className="flex-1 overflow-auto flex flex-wrap gap-2 content-start">
      {previews.length === 0 ? (
        <p style={{ color: "gray", fontSize: 14 }}>등록된 증빙이미지가 없습니다.</p>
      ) : (
        previews.map((preview) => (
          <img
            key={preview.key}
            src={preview.url}
            alt={preview.nm_file}
            title={`${preview.nm_file} (클릭하면 크게 보기)`}
            onClick={() => setEnlarged(preview)}
            style={{
              maxWidth: 360,
              maxHeight: 480,
              border: "1px solid #babfc7",
              cursor: "zoom-in",
            }}
          />
        ))
      )}
      {enlarged && (
        <div
          role="dialog"
          aria-label={`${enlarged.nm_file} 크게 보기`}
          onClick={() => setEnlarged(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            background: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <img
            src={enlarged.url}
            alt={enlarged.nm_file}
            style={{ maxWidth: "90vw", maxHeight: "90vh", background: "#fff" }}
          />
        </div>
      )}
    </div>
  );
};

// 경비상세 증빙이미지: 행에 붙은 사진을 올리고, 이미지면 미리보기로 보여 준다.
const SN_SFA_EXPENSE_REG_001_POP_01 = forwardRef((props: Props, ref) =>
  props.no_expense ? <SavedImages {...props} /> : <PendingImages {...props} />
);

// 저장된 행: 공통 첨부(SnFileUploadBox)로 올리기/내려받기/삭제, 아래에 미리보기
const SavedImages = ({ no_expense, no_line, onCountChanged }: Props) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [previews, setPreviews] = useState<Preview[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const urls: string[] = [];
    const loadPreviews = async () => {
      const { userInfo } = global.getState();
      const { cd_menu, cd_module } = globalMenu.getState().activeMenuInfo;
      const rtn: any = await fnGetDataSet({
        id: "com_attachfile_01",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_menu,
          no_docu: no_expense,
          no_line_docu: no_line,
        },
      });
      const files: any[] = rtn?.[0] ?? [];
      onCountChanged?.(files.length);

      const loaded: Preview[] = [];
      for (const file of files.filter((f) => IMAGE_EXT.test(f.nm_file))) {
        try {
          const res = await axios.post(
            "/snotes/file/download",
            { id: file.id, cdModule: cd_module },
            {
              headers: { "X-TENANT-ID": userInfo.tenant_id || "DEFAULT" },
              responseType: "blob",
            }
          );
          const url = URL.createObjectURL(
            new Blob([res.data], { type: imageMime(file.nm_file) })
          );
          urls.push(url);
          loaded.push({ key: String(file.id), nm_file: file.nm_file, url });
        } catch (error) {
          console.error("증빙이미지 미리보기 실패:", error);
        }
      }
      setPreviews(loaded);
    };
    loadPreviews();
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [no_expense, no_line, reloadKey]);

  const reload = () => setReloadKey((key) => key + 1);

  return (
    <div className="h-full flex flex-col gap-2">
      <div className="flex gap-2 items-start">
        <div ref={boxRef} className="flex-1">
          <SnFileUploadBox
            selectedDocu={no_expense ?? ""}
            selectedLineDocu={no_line ?? 0}
            onUploaded={reload}
            onDeleted={reload}
          />
        </div>
        {/* 공통 첨부의 숨은 파일 입력을 연다(끌어 놓기 외에 파일 선택) */}
        <SnButton
          text="파일 선택"
          onClick={() =>
            boxRef.current
              ?.querySelector<HTMLInputElement>('input[type="file"]')
              ?.click()
          }
        />
      </div>
      <PreviewList previews={previews} />
    </div>
  );
};

// 저장 전 행: 고른 사진은 화면이 들고 있다가 저장 후 채번된 경비번호/항번으로 올린다.
const PendingImages = ({ pendingFiles = [], onPendingChanged }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>(pendingFiles);
  const [previews, setPreviews] = useState<Preview[]>([]);

  useEffect(() => {
    const loaded = files
      .filter((file) => IMAGE_EXT.test(file.name))
      .map((file, i) => ({
        key: `${i}-${file.name}`,
        nm_file: file.name,
        url: URL.createObjectURL(file),
      }));
    setPreviews(loaded);
    return () => loaded.forEach((preview) => URL.revokeObjectURL(preview.url));
  }, [files]);

  const change = (next: File[]) => {
    setFiles(next);
    onPendingChanged?.(next);
  };
  const add = (list: FileList | null) => {
    if (list && list.length > 0) change([...files, ...Array.from(list)]);
  };

  return (
    <div className="h-full flex flex-col gap-2">
      <div className="flex gap-2 items-start">
        <div
          className="flex-1"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            add(e.dataTransfer.files);
          }}
          style={{ border: "1px solid #babfc7", padding: 5, minHeight: 37 }}
        >
          {files.length === 0 ? (
            <p style={{ color: "gray", fontSize: 14, textAlign: "center" }}>
              파일을 마우스로 끌어 오세요. 저장할 때 함께 올라갑니다.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {files.map((file, i) => (
                <span
                  key={`${i}-${file.name}`}
                  style={{ border: "1px solid #babfc7", borderRadius: 3, padding: "2px 6px", fontSize: 13 }}
                >
                  {file.name}{" "}
                  <button
                    type="button"
                    aria-label={`${file.name} 빼기`}
                    onClick={() => change(files.filter((_, j) => j !== i))}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        <SnButton text="파일 선택" onClick={() => inputRef.current?.click()} />
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            add(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      <PreviewList previews={previews} />
    </div>
  );
};

export default SN_SFA_EXPENSE_REG_001_POP_01;
